<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AnyRepurchasedContract, RepurchasedContractInterface } from '../../../../../Types/restructuring'
import useModal from '../../../../../Composables/useModal.ts'
import useLoading from '../../../../../Composables/useLoading.ts'
import useApi from '../../../Composables/useApi'
import ModalDialog from '../../UI/Modal/ModalDialog.vue'
import ButtonAll from '../../UI/Button/ButtonAll.vue'
import { RepurchasedContractType } from '../../../../../Constants/RepurchasedContractType.ts'

const props = defineProps<{
  restructuringId: number
  actionId: number
  contract: AnyRepurchasedContract
}>()

const emit = defineEmits<{
  close: []
  delete: [{ contract: AnyRepurchasedContract }]
}>()

const { t, d, n } = useI18n()
const { deleteJSON } = useApi()
const { loading: sending } = useLoading()

const { modal, modalId, open, close } = useModal({ prefix: 'remove-external-contract-' })

const modalTitleKey = computed(() =>
  props.contract.type === RepurchasedContractType.external
    ? 'restructuring.actions.deleteExternalContract'
    : 'restructuring.actions.deleteContract',
)

async function submit() {
  sending.value = true
  try {
    await deleteJSON(
      `contracts/restructuring/${props.restructuringId}/outstanding-repurchase/${props.actionId}/contracts/${props.contract.id}`,
    )
    emit('delete', { contract: props.contract })
  } finally {
    sending.value = false
  }
}

onMounted(() => {
  open()
})
</script>

<template>
  <ModalDialog ref="modal" :id="modalId" :title-t="modalTitleKey" class-body="px-3" @icon-close="emit('close')">
    <template #body>
      <div class="mt-2">
        <dl>
          <dt>{{ t('restructuring.fields.outstandingRepurchase.contract.number.label') }}</dt>
          <dd>{{ contract.number ?? '-' }}</dd>
          <dt>{{ t('restructuring.fields.outstandingRepurchase.contract.repurchaseDate.label') }}</dt>
          <dd>{{ contract.repurchaseDate ? d(contract.repurchaseDate, 'date') : '-' }}</dd>
          <dt>{{ t('restructuring.fields.outstandingRepurchase.contract.refinancer.label') }}</dt>
          <dd>
            {{
              contract.refinancer?.name ??
              t('restructuring.fields.outstandingRepurchase.contract.refinancer.placeholder')
            }}
          </dd>
          <dt>{{ t('restructuring.fields.outstandingRepurchase.contract.agreementNumber.label') }}</dt>
          <dd>{{ contract.agreementNumber ?? '-' }}</dd>
          <dt>{{ t('restructuring.fields.outstandingRepurchase.contract.repurchaseAmount.label') }}</dt>
          <dd>{{ n(Number(contract.repurchaseAmount), 'currency') }}</dd>
        </dl>
      </div>
    </template>
    <template #footer>
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" @click="close" />
        <ButtonAll
          margin="no"
          type="danger"
          icon="trash"
          label="button.delete"
          class="ml-2"
          :loading="sending"
          @click="submit"
        />
      </div>
    </template>
  </ModalDialog>
</template>
